/**
 * BufferSyncContext - AUI Buffer as Single Source of Truth
 *
 * This context bridges the AUI BufferManager (backend) with the React UI.
 * All content displayed in the Studio comes from buffers managed here.
 *
 * Key principles:
 * - No local content state in components
 * - All content changes go through buffer operations
 * - AUI and GUI always in sync via shared buffer state
 *
 * Operations are decomposed into focused hooks:
 * - useStackOperations: card stack CRUD
 * - useContentHistory: back/forward navigation
 * - useArchiveImport: archive node/thread import
 * - useTransformChain: transform lifecycle
 *
 * @module @humanizer/studio/contexts/BufferSyncContext
 */

import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  type ReactNode,
} from 'react';
import { useApi, type BufferType, type Session } from './ApiContext';
import { useAuth } from './AuthContext';
import { useProject } from './ProjectContext';
import { useStacks } from './StackContext';
import { useToast } from './ToastContext';
import { useArchiveApi } from '../hooks/useArchiveApi';
import {
  contentItemToBufferData,
  bufferTextToContentItems,
  contentItemsToBufferText,
} from '../utils/buffer-bridge';
import type { BreadcrumbEntry } from '../components/workspace/Breadcrumb';
import { createLogger } from '../utils/debug';

// Types and utilities (extracted)
import type {
  ContentItem,
  BufferSummary,
  BufferVersion,
  StackItem,
  ArchiveNode,
  BufferSyncState,
  BufferSyncActions,
  BufferSyncContextType,
  ContentHistoryEntry,
} from './buffer-sync-types';
import {
  DEFAULT_BUFFER_NAME,
  POLL_INTERVAL_MS,
  TAB_SESSION_KEY,
  cleanBufferName,
  electronBufferToSummary,
} from './buffer-sync-utils';

// Decomposed hooks
import { useStackOperations } from '../hooks/useStackOperations';
import { useContentHistory } from '../hooks/useContentHistory';
import { useArchiveImport } from '../hooks/useArchiveImport';
import { useTransformChain } from '../hooks/useTransformChain';

// Re-export types and utilities for backward compatibility
export { cleanBufferName, electronBufferToSummary } from './buffer-sync-utils';
export type { ContentItem, BufferSummary, BufferVersion, StackItem, ArchiveNode } from './buffer-sync-types';

const log = createLogger('BufferSync');

// ═══════════════════════════════════════════════════════════════════════════
// CONTEXT
// ═══════════════════════════════════════════════════════════════════════════

const BufferSyncContext = createContext<BufferSyncContextType | null>(null);

// ═══════════════════════════════════════════════════════════════════════════
// HOOK
// ═══════════════════════════════════════════════════════════════════════════

export function useBufferSync(): BufferSyncContextType {
  const ctx = useContext(BufferSyncContext);
  if (!ctx) {
    throw new Error('useBufferSync must be used within BufferSyncProvider');
  }
  return ctx;
}

/**
 * Convenience hook to just get content
 */
export function useBufferContent(): {
  content: ContentItem[];
  text: string;
  isEmpty: boolean;
  isDirty: boolean;
} {
  const { workingContent, isDirty } = useBufferSync();

  return useMemo(() => ({
    content: workingContent,
    text: workingContent.map(item => item.text).join('\n\n'),
    isEmpty: workingContent.length === 0,
    isDirty,
  }), [workingContent, isDirty]);
}

// ═══════════════════════════════════════════════════════════════════════════
// PROVIDER
// ═══════════════════════════════════════════════════════════════════════════

interface BufferSyncProviderProps {
  children: ReactNode;
  /** Auto-connect on mount */
  autoConnect?: boolean;
  /** Polling interval in ms (0 to disable) */
  pollInterval?: number;
}

export function BufferSyncProvider({
  children,
  autoConnect = true,
  pollInterval = POLL_INTERVAL_MS,
}: BufferSyncProviderProps) {
  const api = useApi();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const archiveApi = useArchiveApi();
  const { activeProject, activeProjectId, createProject, updateProject, addBufferToProject, removeBufferFromProject } = useProject();
  const { activeStackId, ensureWorkingStack } = useStacks();
  const { addToast } = useToast();

  // Electron detection - local-only mode
  const isElectron = typeof window !== 'undefined' && !!window.isElectron;

  // ─────────────────────────────────────────────────────────────────────────
  // STATE
  // ─────────────────────────────────────────────────────────────────────────

  const [sessionId, setSessionId] = useState<string | null>(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);

  const [activeBufferName, setActiveBufferName] = useState(DEFAULT_BUFFER_NAME);
  const [bufferType, setBufferType] = useState<BufferType>('working');
  const [workingContent, setWorkingContent] = useState<ContentItem[]>([]);
  const [isDirty, setIsDirty] = useState(false);

  const [buffers, setBuffers] = useState<BufferSummary[]>([]);
  const [syncError, setSyncError] = useState<string | null>(null);
  const [history, setHistory] = useState<BufferVersion[]>([]);
  const [currentVersionIndex, setCurrentVersionIndex] = useState(0);

  // Navigation state (v12)
  const [breadcrumbTrail, setBreadcrumbTrail] = useState<BreadcrumbEntry[]>([]);
  const [breadcrumbIndex, setBreadcrumbIndex] = useState(0);

  // Version map (v12) — declared but never used; kept for interface compatibility
  const versionMap = useMemo(() => new Map(), []);
  const [activeVersionId, setActiveVersionId] = useState<string | null>(null);

  // Transform chain (linear chain of buffer IDs for VersionBar)
  const [transformChain, setTransformChain] = useState<Array<{ id: string; label: string; type: string; timestamp: number }>>([]);
  const [activeChainBufferId, setActiveChainBufferId] = useState<string | null>(null);

  // Selection state
  const [selectedText, setSelectedText] = useState<string | null>(null);
  const [focusedText, setFocusedText] = useState<string | null>(null);

  // Thread navigation state (for prev/next conversation)
  const [threadIndex, setThreadIndex] = useState<{
    threadIds: string[];
    currentIndex: number;
  } | null>(null);
  const [pendingHighlight, setPendingHighlight] = useState<string | null>(null);
  const [pendingMessageIndex, setPendingMessageIndex] = useState<number | null>(null);

  // Stack mode state
  const [stackItems, setStackItems] = useState<StackItem[]>([]);
  const [activeStackIndex, setActiveStackIndex] = useState(0);

  // Content history (back/forward navigation)
  const contentHistoryRef = useRef<ContentHistoryEntry[]>([]);
  const [contentHistoryIndex, setContentHistoryIndex] = useState(-1);
  const isRestoringHistory = useRef(false);

  // Session log batch queue (v12)
  const logQueueRef = useRef<Array<{ eventType: string; content?: string; metadata?: Record<string, unknown> }>>([]);
  const logFlushTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Workspace state persistence timer (v12)
  const statePersistTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Auto-save debounce timer (5s safety net)
  const autoSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Active buffer ID for Electron persistence
  const activeBufferIdRef = useRef<string | null>(null);

  // Sync ref for workingContent — avoids stale closures in async callbacks
  const workingContentRef = useRef<ContentItem[]>(workingContent);
  workingContentRef.current = workingContent;

  // Ref for addToStack — avoids ordering issues with useCallback definitions
  const addToStackRef = useRef<(item: ContentItem, bufferId?: string) => Promise<void>>(async () => {});

  // Guard against concurrent buffer creates for same content
  const pendingAddsRef = useRef<Set<string>>(new Set());

  // Per-card debounced save timers (A3a: edit → save buffer)
  const stackSaveTimerRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  // Debounced reorder sync timer (A3c)
  const reorderTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Stack restoration flag — prevents M6 useEffect from re-adding buffers during restore
  const stackRestoredRef = useRef(false);

  // Track previous activeStackId for stack switching
  const prevActiveStackIdRef = useRef<string | null | undefined>(undefined);

  const isConnected = sessionId !== null && !connectionError;

  // ─────────────────────────────────────────────────────────────────────────
  // CONNECTION
  // ─────────────────────────────────────────────────────────────────────────

  const connect = useCallback(async () => {
    if (isConnecting || sessionId) return;

    setIsConnecting(true);
    setConnectionError(null);

    try {
      // ELECTRON: Local-only mode - no cloud session needed
      if (isElectron) {
        const localSessionId = `local-${Date.now()}`;
        setSessionId(localSessionId);

        // Load buffer list from DB (don't auto-load content — let user choose)
        if (window.electronBuffer) {
          try {
            const allBuffers = await window.electronBuffer.list({ limit: 50 });
            const summaries = allBuffers.map(electronBufferToSummary);
            setBuffers(summaries);
          } catch (err) {
            log.warn('Failed to load buffers on connect:', err);
            setBuffers([]);
          }

          // Restore content history stack from electron-store
          if (window.electronAPI?.store) {
            try {
              const [savedStack, savedIndex] = await Promise.all([
                window.electronAPI.store.get('contentHistoryStack'),
                window.electronAPI.store.get('contentHistoryIndex'),
              ]);
              if (Array.isArray(savedStack) && savedStack.length > 0) {
                contentHistoryRef.current = savedStack as Array<{ bufferId: string; label: string }>;
                setContentHistoryIndex(typeof savedIndex === 'number' ? savedIndex : savedStack.length - 1);
              }
            } catch {
              // History restoration is best-effort
            }
          }
        } else {
          setWorkingContent([]);
        }

        setIsConnecting(false);
        return;
      }

      let resumedSession: Session | null = null;

      // Tab-scoped session isolation: check sessionStorage first (prevents cross-tab bleed)
      const tabSessionId = sessionStorage.getItem(TAB_SESSION_KEY);
      if (tabSessionId) {
        try {
          const existing = await api.getSession(tabSessionId);
          if (existing?.id) {
            resumedSession = existing;
          }
        } catch {
          // Tab session expired or invalid — fall through
          sessionStorage.removeItem(TAB_SESSION_KEY);
        }
      }

      // Fallback: resume from server-stored "last used" session (fresh tab only)
      if (!resumedSession) {
        try {
          const { value } = await api.workspaceState.get('active_session_id');
          if (value && typeof value === 'string') {
            const existing = await api.getSession(value);
            if (existing?.id) {
              resumedSession = existing;
            }
          }
        } catch {
          // No saved session or fetch failed — create fresh
        }
      }

      const session = resumedSession ?? await api.createSession({ name: 'Studio Session' });
      setSessionId(session.id);

      // Store in tab-scoped sessionStorage (primary) and server (fallback for fresh tabs)
      sessionStorage.setItem(TAB_SESSION_KEY, session.id);
      api.workspaceState.set('active_session_id', session.id).catch(() => {});

      // Create default workspace buffer
      try {
        await api.createBuffer(session.id, DEFAULT_BUFFER_NAME, []);
      } catch {
        // Buffer might already exist, that's OK
      }

      // Load user-scoped persistent buffers and mirror to session
      try {
        const userResult = await api.userBuffers.list();
        for (const ub of userResult.buffers) {
          try {
            await api.createBuffer(session.id, ub.name, []);
          } catch {
            // Session mirror already exists
          }
        }
      } catch {
        // User buffers fetch failed — continue with session-only
      }

      // Load buffer list
      const result = await api.listBuffers(session.id);
      setBuffers(result.buffers);

      // Load workspace content
      try {
        const buffer = await api.getBuffer(session.id, DEFAULT_BUFFER_NAME);
        setWorkingContent((buffer.workingContent ?? buffer.content ?? []) as ContentItem[]);
        setIsDirty(false); // Fresh from server = not dirty
      } catch {
        setWorkingContent([]);
      }

      // Restore breadcrumb and recent buffers from workspace state
      try {
        const [bcResult, rbResult] = await Promise.all([
          api.workspaceState.get('breadcrumb_stack'),
          api.workspaceState.get('recent_buffers'),
        ]);
        if (bcResult.value && Array.isArray(bcResult.value)) {
          setBreadcrumbTrail(bcResult.value as BreadcrumbEntry[]);
        }
        // recent_buffers stored for future use but not wired into UI state yet
        void rbResult;
      } catch {
        // State restoration is best-effort
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Connection failed';
      setConnectionError(message);
      log.error('Connection failed:', error);
    } finally {
      setIsConnecting(false);
    }
  }, [api, isConnecting, sessionId]);

  const disconnect = useCallback(() => {
    if (sessionId && !isElectron) {
      api.deleteSession(sessionId).catch(log.error);
    }
    setSessionId(null);
    setWorkingContent([]);
    setBuffers([]);
    setHistory([]);
    setConnectionError(null);
  }, [api, sessionId, isElectron]);

  // Auto-connect when authenticated (wait for auth to finish loading)
  // ELECTRON: Connect immediately without requiring cloud auth
  useEffect(() => {
    if (autoConnect) {
      if (isElectron) {
        // Electron: connect immediately (local-only mode)
        connect();
      } else if (isAuthenticated && !authLoading) {
        // Web: wait for authentication
        connect();
      }
    }
    return () => {
      // Don't disconnect on unmount - session persists
    };
  }, [autoConnect, isAuthenticated, authLoading, isElectron]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─────────────────────────────────────────────────────────────────────────
  // BUFFER MANAGEMENT
  // ─────────────────────────────────────────────────────────────────────────

  const refreshBuffers = useCallback(async () => {
    if (!sessionId) return;

    // ELECTRON: Load from local DB
    if (isElectron) {
      if (!window.electronBuffer) return;
      try {
        const allBuffers = await window.electronBuffer.list({ limit: 50 });
        setBuffers(allBuffers.map(electronBufferToSummary));
      } catch (err) {
        log.error('Failed to refresh Electron buffers:', err);
      }
      return;
    }

    try {
      const result = await api.listBuffers(sessionId);
      setBuffers(result.buffers);
    } catch (error) {
      log.error('Failed to refresh buffers:', error);
    }
  }, [api, sessionId, isElectron]);

  const createBuffer = useCallback(async (name: string, content: ContentItem[] = [], type?: BufferType) => {
    if (!sessionId) throw new Error('Not connected');

    // ELECTRON: Local-only mode - just update local state
    if (isElectron) {
      setBuffers(prev => [...prev, { name, version: '1', currentBranch: 'main', contentLength: content.length, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() }]);
      return;
    }

    // Create user-scoped persistent buffer first
    try {
      await api.userBuffers.create({ name, type: type ?? 'working', content });
    } catch {
      // May already exist or user buffers unavailable — continue with session
    }

    await api.createBuffer(sessionId, name, content);
    await refreshBuffers();
  }, [api, sessionId, refreshBuffers, isElectron]);

  const switchBuffer = useCallback(async (name: string) => {
    if (!sessionId) throw new Error('Not connected');

    // ELECTRON: Load buffer by ID from DB
    if (isElectron) {
      // Find the buffer summary to get the ID
      const summary = buffers.find(b => b.name === name || b.id === name);
      const bufferId = summary?.id ?? name;

      setActiveBufferName(summary?.name ?? name);
      setSelectedText(null);

      if (window.electronBuffer && bufferId) {
        try {
          const buffer = await window.electronBuffer.load(bufferId);
          if (buffer) {
            activeBufferIdRef.current = buffer.id;
            const items = bufferTextToContentItems(buffer.text, buffer.origin);
            setWorkingContent(items);
            setIsDirty(false);
            // Refresh transform chain for this buffer
            try {
              const chain = await window.electronBuffer.getChain(buffer.id);
              setTransformChain(chain);
              setActiveChainBufferId(buffer.id);
            } catch { /* chain is best-effort */ }
            return;
          }
        } catch (err) {
          log.warn('Failed to load Electron buffer:', err);
        }
      }
      setWorkingContent([]);
      setIsDirty(false);
      return;
    }

    try {
      // Try loading from user-scoped buffer first (persistent source of truth)
      let content: ContentItem[] | null = null;
      if (name !== DEFAULT_BUFFER_NAME) {
        try {
          const userBufs = await api.userBuffers.list();
          const match = userBufs.buffers.find(b => b.name === name);
          if (match) {
            const voiceOrOther = await api.userBuffers.getVoice();
            // For voice buffer, use the dedicated endpoint
            if (voiceOrOther.name === name) {
              content = (voiceOrOther.workingContent ?? []) as ContentItem[];
            }
          }
        } catch {
          // Fall back to session buffer
        }
      }

      const buffer = await api.getBuffer(sessionId, name);
      setActiveBufferName(name);
      setWorkingContent(content ?? (buffer.workingContent ?? buffer.content ?? []) as ContentItem[]);
      setIsDirty(false);
      setSelectedText(null);

      // Load history
      const historyResult = await api.getBufferHistory(sessionId, name, 20);
      setHistory(historyResult.history as BufferVersion[]);
      setCurrentVersionIndex(0);
    } catch (error) {
      log.error('Failed to switch buffer:', error);
      throw error;
    }
  }, [api, sessionId, isElectron, buffers]);

  const deleteBuffer = useCallback(async (name: string, force?: boolean) => {
    if (!sessionId) throw new Error('Not connected');
    if (name === DEFAULT_BUFFER_NAME) {
      throw new Error('Cannot delete the workspace buffer');
    }

    // ELECTRON: Delete from DB and update state
    if (isElectron) {
      const summary = buffers.find(b => b.name === name || b.id === name);
      const bufferId = summary?.id ?? name;
      if (window.electronBuffer && bufferId) {
        try {
          await window.electronBuffer.delete(bufferId);
        } catch (err) {
          log.warn('Electron buffer delete failed:', err);
        }
      }
      setBuffers(prev => prev.filter(b => b.name !== name && b.id !== name));
      if (name === activeBufferName || (summary?.id && activeBufferIdRef.current === summary.id)) {
        setActiveBufferName(DEFAULT_BUFFER_NAME);
        activeBufferIdRef.current = null;
        setWorkingContent([]);
        setIsDirty(false);
      }
      return;
    }

    // Delete session mirror
    await api.deleteBuffer(sessionId, name, force);

    // Also delete user-scoped persistent buffer (best-effort)
    api.userBuffers.updateContent(name, []).catch(() => {});

    await refreshBuffers();

    // If we deleted the active buffer, switch to workspace
    if (name === activeBufferName) {
      await switchBuffer(DEFAULT_BUFFER_NAME);
    }
  }, [api, sessionId, activeBufferName, refreshBuffers, switchBuffer, isElectron]);

  const pinBuffer = useCallback(async (name: string, pinned: boolean) => {
    if (!sessionId) throw new Error('Not connected');
    if (isElectron) return;
    await api.pinBuffer(sessionId, name, pinned);
    await refreshBuffers();
  }, [api, sessionId, refreshBuffers, isElectron]);

  const branchBuffer = useCallback(async (name: string, branchName: string) => {
    if (!sessionId) throw new Error('Not connected');
    if (isElectron) return;
    await api.branchBuffer(sessionId, name, branchName);
  }, [api, sessionId, isElectron]);

  const switchBranchAction = useCallback(async (name: string, branchName: string) => {
    if (!sessionId) throw new Error('Not connected');
    if (isElectron) return;
    await api.switchBranch(sessionId, name, branchName);
    // Reload buffer content after branch switch
    const buffer = await api.getBuffer(sessionId, name);
    setWorkingContent((buffer.workingContent ?? buffer.content ?? []) as ContentItem[]);
    setIsDirty(false);
  }, [api, sessionId, isElectron]);

  const rollbackBufferAction = useCallback(async (name: string, steps?: number) => {
    if (!sessionId) throw new Error('Not connected');
    if (isElectron) return;
    await api.rollbackBuffer(sessionId, name, steps);
    // Reload buffer content after rollback
    const buffer = await api.getBuffer(sessionId, name);
    setWorkingContent((buffer.workingContent ?? buffer.content ?? []) as ContentItem[]);
    setIsDirty(false);
    // Reload history
    const historyResult = await api.getBufferHistory(sessionId, name, 20);
    setHistory(historyResult.history as BufferVersion[]);
  }, [api, sessionId, isElectron]);

  // ─────────────────────────────────────────────────────────────────────────
  // CONTENT OPERATIONS
  // ─────────────────────────────────────────────────────────────────────────

  const setContent = useCallback(async (content: ContentItem[]) => {
    if (!sessionId) throw new Error('Not connected');

    // Optimistic update
    setWorkingContent(content);
    setIsDirty(true);

    // ELECTRON: Persist via buffer IPC — serialize all items into single buffer
    if (isElectron) {
      if (window.electronBuffer && content.length > 0) {
        try {
          const serializedText = contentItemsToBufferText(content);
          const firstItem = content[0];
          if (activeBufferIdRef.current) {
            // Load existing buffer, update text with all items, re-save
            const existing = await window.electronBuffer.load(activeBufferIdRef.current);
            const partial = contentItemToBufferData(
              { ...firstItem, text: serializedText },
              existing
            );
            await window.electronBuffer.save(partial);
          } else {
            // Create new buffer from serialized text
            const saved = await window.electronBuffer.createFromText(serializedText, {
              format: 'markdown',
              initialState: 'staged',
              sourcePlatform: firstItem.metadata.source?.type,
              author: firstItem.metadata.author,
              authorRole: firstItem.metadata.authorRole,
              metadata: {
                threadId: firstItem.metadata.source?.threadId,
                threadTitle: firstItem.metadata.source?.threadTitle,
                nodeId: firstItem.metadata.source?.nodeId,
                messageCount: content.length,
              },
            });
            activeBufferIdRef.current = saved.id;
          }
          setSyncError(null);
          // Auto-save timer: commit buffer after 5s of no further edits
          if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
          autoSaveTimerRef.current = setTimeout(() => {
            if (activeBufferIdRef.current && window.electronBuffer) {
              window.electronBuffer.commit(activeBufferIdRef.current).catch(() => {});
              setIsDirty(false);
            }
          }, 5000);
        } catch (err) {
          log.warn('Electron buffer save failed:', err);
          setSyncError('Buffer save failed — changes may not persist');
        }
      }
      return;
    }

    try {
      await api.setBufferContent(sessionId, activeBufferName, content);
    } catch (error) {
      // Rollback on error
      log.error('Failed to set content:', error);
      // Reload from server
      const buffer = await api.getBuffer(sessionId, activeBufferName);
      setWorkingContent((buffer.workingContent ?? buffer.content ?? []) as ContentItem[]);
      setIsDirty(false);
      throw error;
    }

    // Write-through to persistent user buffer (fire-and-forget)
    if (activeBufferName !== DEFAULT_BUFFER_NAME) {
      api.userBuffers.updateContent(activeBufferName, content).then(
        () => setSyncError(null),
        (err) => {
          log.warn('Persistent write-through failed:', err);
          setSyncError('Changes not saved to persistent storage');
        }
      );
    }
  }, [api, sessionId, activeBufferName, isElectron]);

  const appendContent = useCallback(async (items: ContentItem[]) => {
    if (!sessionId) throw new Error('Not connected');

    // Optimistic update
    setWorkingContent(prev => [...prev, ...items]);
    setIsDirty(true);

    // ELECTRON: Persist appended content via buffer IPC — re-serialize full content
    if (isElectron) {
      if (window.electronBuffer && items.length > 0) {
        try {
          // Use ref for latest state — avoids stale closure on rapid appends
          const allContent = [...workingContentRef.current, ...items];
          const serializedText = contentItemsToBufferText(allContent);
          if (activeBufferIdRef.current) {
            const existing = await window.electronBuffer.load(activeBufferIdRef.current);
            if (existing) {
              const partial = contentItemToBufferData(
                { ...items[0], text: serializedText },
                existing
              );
              await window.electronBuffer.save(partial);
            }
          } else {
            const saved = await window.electronBuffer.createFromText(serializedText, {
              format: 'markdown',
              initialState: 'staged',
            });
            activeBufferIdRef.current = saved.id;
          }
          setSyncError(null);
        } catch (err) {
          log.warn('Electron appendContent save failed:', err);
          setSyncError('Buffer save failed — changes may not persist');
        }
      }
      return;
    }

    try {
      await api.appendToBuffer(sessionId, activeBufferName, items);
    } catch (error) {
      log.error('Failed to append content:', error);
      // Reload from server
      const buffer = await api.getBuffer(sessionId, activeBufferName);
      setWorkingContent((buffer.workingContent ?? buffer.content ?? []) as ContentItem[]);
      throw error;
    }

    // Write-through: sync full content to persistent user buffer
    if (activeBufferName !== DEFAULT_BUFFER_NAME) {
      const fullContent = [...workingContentRef.current, ...items];
      api.userBuffers.updateContent(activeBufferName, fullContent).then(
        () => setSyncError(null),
        (err) => {
          log.warn('Persistent write-through failed:', err);
          setSyncError('Changes not saved to persistent storage');
        }
      );
    }
  }, [api, sessionId, activeBufferName, isElectron]);

  const clearContent = useCallback(async () => {
    await setContent([]);
  }, [setContent]);

  const updateItem = useCallback(async (id: string, updates: Partial<ContentItem>) => {
    const newContent = workingContent.map(item =>
      item.id === id ? { ...item, ...updates } : item
    );
    await setContent(newContent);
  }, [workingContent, setContent]);

  const removeItem = useCallback(async (id: string) => {
    const newContent = workingContent.filter(item => item.id !== id);
    await setContent(newContent);
  }, [workingContent, setContent]);

  // ─────────────────────────────────────────────────────────────────────────
  // VERSION CONTROL
  // ─────────────────────────────────────────────────────────────────────────

  const commit = useCallback(async (message: string) => {
    if (!sessionId) throw new Error('Not connected');

    // ELECTRON: Commit via buffer IPC (transitions state + auto-embeds)
    if (isElectron) {
      if (window.electronBuffer && activeBufferIdRef.current) {
        try {
          await window.electronBuffer.commit(activeBufferIdRef.current);
        } catch (err) {
          log.warn('Electron buffer commit failed:', err);
        }
      }
      setIsDirty(false);
      return;
    }

    if (!isDirty) return;

    try {
      await api.commitBuffer(sessionId, activeBufferName, message);
      setIsDirty(false);

      // Reload history
      const historyResult = await api.getBufferHistory(sessionId, activeBufferName, 20);
      setHistory(historyResult.history as BufferVersion[]);
      setCurrentVersionIndex(0);
    } catch (error) {
      log.error('Failed to commit:', error);
      throw error;
    }
  }, [api, sessionId, activeBufferName, isDirty, isElectron]);

  const undo = useCallback(async () => {
    if (!sessionId || history.length < 2) return;

    const nextIndex = Math.min(currentVersionIndex + 1, history.length - 1);
    const version = history[nextIndex];

    if (version) {
      setCurrentVersionIndex(nextIndex);
    }
  }, [sessionId, history, currentVersionIndex]);

  const redo = useCallback(async () => {
    if (!sessionId || currentVersionIndex <= 0) return;

    const nextIndex = currentVersionIndex - 1;
    const version = history[nextIndex];

    if (version) {
      setCurrentVersionIndex(nextIndex);
    }
  }, [sessionId, history, currentVersionIndex]);

  const checkout = useCallback(async (versionId: string) => {
    if (!sessionId) throw new Error('Not connected');

    // Find version in history
    const index = history.findIndex(v => v.id === versionId);
    if (index >= 0) {
      setCurrentVersionIndex(index);
    }
  }, [sessionId, history]);

  const canUndo = history.length > 1 && currentVersionIndex < history.length - 1;
  const canRedo = currentVersionIndex > 0;

  // ─────────────────────────────────────────────────────────────────────────
  // DECOMPOSED HOOKS
  // ─────────────────────────────────────────────────────────────────────────

  // Content history (back/forward)
  const contentHistory = useContentHistory({
    isElectron,
    contentHistoryRef,
    contentHistoryIndex,
    setContentHistoryIndex,
    isRestoringHistory,
    activeBufferIdRef,
    workingContent,
    setContent,
    setSyncError,
  });

  // Archive import
  const archiveImport = useArchiveImport({
    isElectron,
    api: { overrides: api.overrides },
    archiveApi: { isLocal: archiveApi.isLocal },
    activeBufferIdRef,
    setActiveBufferName,
    setWorkingContent,
    setIsDirty,
    setSyncError,
    workingContentRef,
    setContent,
    appendContent,
    commit,
    isRestoringHistory,
    pushContentHistory: contentHistory.pushContentHistory,
    setTransformChain,
    setActiveChainBufferId,
    addToStackRef,
  });

  // Transform chain
  const transformChainOps = useTransformChain({
    isElectron,
    sessionId,
    api: { transform: api.transform },
    activeBufferIdRef,
    workingContent,
    setWorkingContent,
    setIsDirty,
    setContent,
    commit,
    transformChain,
    setTransformChain,
    setActiveChainBufferId,
  });

  // Stack operations
  const stackOps = useStackOperations({
    isElectron,
    stackItems,
    setStackItems,
    activeStackIndex,
    setActiveStackIndex,
    activeBufferIdRef,
    workingContentRef,
    pendingAddsRef,
    stackSaveTimerRef,
    reorderTimerRef,
    stackRestoredRef,
    activeProjectId,
    activeStackId,
    addBufferToProject,
    removeBufferFromProject,
    ensureWorkingStack,
  });
  addToStackRef.current = stackOps.addToStack;

  // ─────────────────────────────────────────────────────────────────────────
  // CONTEXTUAL CONTENT OPENING
  // ─────────────────────────────────────────────────────────────────────────

  const openNodeLoadingRef = useRef(false);

  const openNodeInContext = useCallback(async (opts: {
    nodeId: string;
    highlightText?: string;
    threadList?: string[];
    threadListIndex?: number;
  }) => {
    // Guard: validate nodeId before making API call
    if (!opts.nodeId) {
      log.warn('openNodeInContext called without nodeId');
      return;
    }

    // Guard: prevent concurrent loads (rapid clicks)
    if (openNodeLoadingRef.current) return;
    openNodeLoadingRef.current = true;

    try {
      // 1. Fetch the node to get threadRootId
      const nodeResult = await archiveApi.getNode(opts.nodeId);
      const node = nodeResult.node;

      if (!node) {
        log.error('Node not found:', opts.nodeId);
        return;
      }

      const threadRootId = node.threadRootId ?? opts.nodeId;
      const threadTitle = node.sourceTitle ?? node.title ?? 'Thread';

      // 2. Fetch full thread
      const threadResult = await archiveApi.getThread(threadRootId);
      let threadNodes = threadResult.nodes;

      // Fallback: standalone nodes (e.g. imported markdown/document nodes)
      // have no thread_root_id and no children — display the single node.
      if ((!threadNodes || threadNodes.length === 0) && node) {
        threadNodes = [node];
      }

      if (!threadNodes || threadNodes.length === 0) return;

      // 2b. Get thread root's sourceMetadata (conversation-level context: participants, thread_type, etc.)
      let threadSourceMetadata: Record<string, unknown> | undefined;
      if (threadRootId !== opts.nodeId) {
        // Thread root is a separate node — fetch its metadata
        try {
          const rootResult = await archiveApi.getNode(threadRootId);
          threadSourceMetadata = (rootResult.node as any)?.sourceMetadata as Record<string, unknown> | undefined;
        } catch { /* non-critical */ }
      } else {
        threadSourceMetadata = (node as any)?.sourceMetadata;
      }

      // 3. Map to ArchiveNode[]
      const archiveNodes: ArchiveNode[] = threadNodes.map((n: any) => ({
        id: n.id,
        text: n.text || '',
        type: 'message' as const,
        sourceType: n.sourceType,
        threadId: threadRootId,
        threadTitle,
        authorRole: n.authorRole as ArchiveNode['authorRole'],
        timestamp: n.sourceCreatedAt,
        wordCount: n.wordCount ?? (n.text || '').split(/\s+/).filter(Boolean).length,
        mediaRefs: n.mediaRefs,
        sourceMetadata: n.sourceMetadata || threadSourceMetadata,
      }));

      // 4. Import thread into buffer
      await archiveImport.importArchiveThread({ id: threadRootId, title: threadTitle }, archiveNodes);

      // 4b. Auto-add to stack only for standalone/single-message nodes
      if (archiveNodes.length === 1) {
        const totalWords = archiveNodes[0].wordCount ?? archiveNodes[0].text.split(/\s+/).filter(Boolean).length;
        const threadSummaryItem: ContentItem = {
          id: crypto.randomUUID(),
          type: 'archive-node',
          text: archiveNodes[0]?.text ?? '',
          metadata: {
            source: {
              type: archiveNodes[0]?.sourceType ?? 'archive',
              path: [threadTitle],
              nodeId: opts.nodeId,
              threadId: threadRootId,
              threadTitle,
            },
            timestamp: archiveNodes[0]?.timestamp,
            wordCount: totalWords,
          },
        };
        await addToStackRef.current(threadSummaryItem, activeBufferIdRef.current ?? undefined);
      }

      // 5. Find which message the clicked node is in
      const targetIndex = threadNodes.findIndex((n: any) => n.id === opts.nodeId);
      setPendingMessageIndex(targetIndex >= 0 ? targetIndex : 0);

      // 6. Set highlight
      if (opts.highlightText) {
        setPendingHighlight(opts.highlightText);
      }

      // 7. Set thread navigation index
      if (opts.threadList && opts.threadListIndex !== undefined) {
        setThreadIndex({
          threadIds: opts.threadList,
          currentIndex: opts.threadListIndex,
        });
      } else {
        setThreadIndex(null);
      }
    } catch (error) {
      log.error('openNodeInContext failed:', error);
      setSyncError('Failed to load content from archive');
    } finally {
      openNodeLoadingRef.current = false;
    }
  }, [archiveApi, archiveImport.importArchiveThread]);

  const goToPrevThread = useCallback(async () => {
    if (!threadIndex || threadIndex.currentIndex <= 0) return;
    const newIndex = threadIndex.currentIndex - 1;
    const threadId = threadIndex.threadIds[newIndex];
    try {
      const threadResult = await archiveApi.getThread(threadId);
      if (threadResult.nodes?.length > 0) {
        await openNodeInContext({
          nodeId: threadResult.nodes[0].id,
          threadList: threadIndex.threadIds,
          threadListIndex: newIndex,
        });
      }
    } catch (error) {
      log.error('goToPrevThread failed:', error);
    }
  }, [threadIndex, archiveApi, openNodeInContext]);

  const goToNextThread = useCallback(async () => {
    if (!threadIndex || threadIndex.currentIndex >= threadIndex.threadIds.length - 1) return;
    const newIndex = threadIndex.currentIndex + 1;
    const threadId = threadIndex.threadIds[newIndex];
    try {
      const threadResult = await archiveApi.getThread(threadId);
      if (threadResult.nodes?.length > 0) {
        await openNodeInContext({
          nodeId: threadResult.nodes[0].id,
          threadList: threadIndex.threadIds,
          threadListIndex: newIndex,
        });
      }
    } catch (error) {
      log.error('goToNextThread failed:', error);
    }
  }, [threadIndex, archiveApi, openNodeInContext]);

  const clearPendingNavigation = useCallback(() => {
    setPendingHighlight(null);
    setPendingMessageIndex(null);
  }, []);

  // ─────────────────────────────────────────────────────────────────────────
  // AUTO-PROJECT CREATION + AUI NAMING (M6)
  // ─────────────────────────────────────────────────────────────────────────

  // Track whether we've already requested naming for this project
  const namingSuggestedRef = useRef(false);

  useEffect(() => {
    const len = stackItems.length;
    if (len === 0) return;

    // Skip auto-project creation during stack restoration
    if (stackRestoredRef.current && len > 1) return;

    // First card added and no active project → auto-create
    if (len === 1 && !activeProjectId) {
      createProject('Untitled Project').catch((err) => {
        log.warn('Auto-project creation failed:', err);
      });
    }

    // 3rd card added → suggest a name via AUI
    if (len === 3 && activeProject?.name === 'Untitled Project' && !namingSuggestedRef.current) {
      namingSuggestedRef.current = true;

      if (isElectron && window.electronHarvest?.suggestProjectName) {
        const titles = stackItems.map(s => s.summary);
        window.electronHarvest.suggestProjectName(titles).then(({ suggestedName }) => {
          if (suggestedName && activeProject) {
            addToast(`Rename project to "${suggestedName}"?`, 'info', {
              duration: 8000,
              action: {
                label: 'Rename',
                onClick: () => {
                  updateProject(activeProject.id, { name: suggestedName }).catch(() => {});
                },
              },
            });
          }
        }).catch(() => {});
      }
    }
  }, [stackItems.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset naming suggestion flag when project changes
  useEffect(() => {
    if (!activeProjectId) {
      namingSuggestedRef.current = false;
    }
  }, [activeProjectId]);

  const getUserBuffers = useCallback(async (type?: BufferType) => {
    if (isElectron) return [];
    const result = await api.userBuffers.list(type);
    return result.buffers;
  }, [api, isElectron]);

  const getVoiceBuffer = useCallback(async () => {
    if (isElectron) return { name: 'voice', version: '1', currentBranch: 'main', workingContent: [], content: [], isDirty: false, branches: {}, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() } satisfies import('./ApiContext').Buffer;
    return api.userBuffers.getVoice();
  }, [api, isElectron]);

  // ─────────────────────────────────────────────────────────────────────────
  // NAVIGATION (v12)
  // ─────────────────────────────────────────────────────────────────────────

  const navigateTo = useCallback((entry: BreadcrumbEntry) => {
    setBreadcrumbTrail(prev => {
      const newTrail = prev.slice(breadcrumbIndex);
      return [entry, ...newTrail];
    });
    setBreadcrumbIndex(0);
  }, [breadcrumbIndex]);

  const navigateBack = useCallback(() => {
    setBreadcrumbIndex(prev => Math.min(prev + 1, breadcrumbTrail.length - 1));
  }, [breadcrumbTrail.length]);

  const navigateForward = useCallback(() => {
    setBreadcrumbIndex(prev => Math.max(prev - 1, 0));
  }, []);

  // ─────────────────────────────────────────────────────────────────────────
  // VERSION SWITCHING (v12)
  // ─────────────────────────────────────────────────────────────────────────

  const selectVersion = useCallback((versionId: string) => {
    setActiveVersionId(versionId);
  }, []);

  // ─────────────────────────────────────────────────────────────────────────
  // SESSION LOGGING (v12) - batched, non-blocking
  // ─────────────────────────────────────────────────────────────────────────

  const flushLogs = useCallback(async () => {
    if (!sessionId || logQueueRef.current.length === 0) return;
    // ELECTRON: No cloud session logging
    if (isElectron) {
      logQueueRef.current = [];
      return;
    }

    const entries = [...logQueueRef.current];
    logQueueRef.current = [];

    try {
      await fetch(`/api/sessions/${sessionId}/logs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ entries }),
      });
    } catch {
      // Re-queue on failure
      logQueueRef.current.unshift(...entries);
    }
  }, [sessionId, isElectron]);

  const logEvent = useCallback((
    eventType: string,
    content?: string,
    metadata?: Record<string, unknown>
  ) => {
    logQueueRef.current.push({ eventType, content, metadata });

    // Auto-flush every 5s
    if (!logFlushTimerRef.current) {
      logFlushTimerRef.current = setTimeout(() => {
        logFlushTimerRef.current = null;
        flushLogs();
      }, 5000);
    }
  }, [flushLogs]);

  // Flush on unmount / beforeunload
  useEffect(() => {
    const handleBeforeUnload = () => flushLogs();
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      flushLogs();
      if (logFlushTimerRef.current) clearTimeout(logFlushTimerRef.current);
    };
  }, [flushLogs]);

  // ─────────────────────────────────────────────────────────────────────────
  // WORKSPACE STATE PERSISTENCE (v12) - debounced
  // ─────────────────────────────────────────────────────────────────────────

  const persistState = useCallback((key: string, value: unknown) => {
    // ELECTRON: Skip cloud persistence in local-only mode
    if (isElectron) return;

    if (statePersistTimerRef.current) {
      clearTimeout(statePersistTimerRef.current);
    }
    statePersistTimerRef.current = setTimeout(() => {
      statePersistTimerRef.current = null;
      api.workspaceState.set(key, value).catch(() => {});
    }, 500);
  }, [api, isElectron]);

  // Persist breadcrumb trail on change
  useEffect(() => {
    if (breadcrumbTrail.length > 0) {
      persistState('breadcrumb_stack', breadcrumbTrail);
    }
  }, [breadcrumbTrail, persistState]);

  // Persist active buffer name on switch
  useEffect(() => {
    if (sessionId && activeBufferName !== DEFAULT_BUFFER_NAME) {
      persistState('recent_buffers', [activeBufferName]);
    }
  }, [activeBufferName, sessionId, persistState]);

  // Clean up persist timer, auto-save timer, and stack save timers on unmount
  useEffect(() => {
    return () => {
      if (statePersistTimerRef.current) clearTimeout(statePersistTimerRef.current);
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
      if (reorderTimerRef.current) clearTimeout(reorderTimerRef.current);
      for (const timer of stackSaveTimerRef.current.values()) clearTimeout(timer);
      stackSaveTimerRef.current.clear();
    };
  }, []);

  // Before-quit flush: commit dirty buffer + flush pending stack saves
  useEffect(() => {
    if (!isElectron) return;
    const handleFlush = () => {
      if (activeBufferIdRef.current && window.electronBuffer) {
        window.electronBuffer.commit(activeBufferIdRef.current).catch(() => {});
      }
      // Flush all pending stack card saves immediately
      for (const [bid, timer] of stackSaveTimerRef.current.entries()) {
        clearTimeout(timer);
        stackSaveTimerRef.current.delete(bid);
        // Best-effort immediate save — can't await in event handler
        const item = stackItems.find(s => s.bufferId === bid);
        if (item && window.electronBuffer) {
          window.electronBuffer.load(bid).then(buffer => {
            if (buffer) {
              window.electronBuffer!.save({ ...buffer, text: item.contentItem.text, wordCount: item.wordCount }).catch(() => {});
            }
          }).catch(() => {});
        }
      }
    };
    window.addEventListener('app:flush-pending', handleFlush);
    return () => window.removeEventListener('app:flush-pending', handleFlush);
  }, [isElectron, stackItems]);

  // ─────────────────────────────────────────────────────────────────────────
  // STACK SWITCHING — clear workspace when user picks a different named stack
  // ─────────────────────────────────────────────────────────────────────────

  useEffect(() => {
    // Skip first mount (undefined → initial value)
    if (prevActiveStackIdRef.current === undefined) {
      prevActiveStackIdRef.current = activeStackId;
      return;
    }
    if (activeStackId === prevActiveStackIdRef.current) return;
    prevActiveStackIdRef.current = activeStackId;

    // Switching to a different stack (or clearing) — reset workspace for reload
    setStackItems([]);
    setActiveStackIndex(0);
    stackRestoredRef.current = false;
  }, [activeStackId]);

  // ─────────────────────────────────────────────────────────────────────────
  // STACK RESTORATION (A2) — Rebuild stack from project buffers on launch
  // ─────────────────────────────────────────────────────────────────────────

  useEffect(() => {
    log.log('Stack restore check:', { isElectron, isConnected, activeProjectId, activeStackId, restored: stackRestoredRef.current, stackLen: stackItems.length });
    if (!isElectron || !isConnected || !activeProjectId || stackRestoredRef.current) return;
    // Only restore into an empty stack (don't overwrite user's in-progress work)
    if (stackItems.length > 0) {
      stackRestoredRef.current = true;
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        if (!window.electronBuffer) {
          log.warn('Stack restore: missing buffer bridge');
          return;
        }

        // Use named stack hydration when available, fall back to project buffers
        let bufferIds: string[] = [];
        if (activeStackId && window.electronStack) {
          const hydration = await window.electronStack.hydrate(activeStackId);
          bufferIds = hydration?.buffers?.map(b => b.bufferId) ?? [];
        } else if (window.electronHarvest?.listProjectBuffers) {
          bufferIds = await window.electronHarvest.listProjectBuffers(activeProjectId) ?? [];
        }
        log.log('Stack restore: has', bufferIds.length, 'buffers');
        if (cancelled || !bufferIds || bufferIds.length === 0) return;

        // Load all buffers in parallel
        const loadResults = await Promise.all(
          bufferIds.map(id => window.electronBuffer!.load(id).catch(() => null))
        );

        if (cancelled) return;

        // Reconstruct StackItems from buffers (preserving sort order from project)
        const restoredItems: StackItem[] = [];
        for (let i = 0; i < loadResults.length; i++) {
          const buffer = loadResults[i];
          if (!buffer) continue; // Deleted buffer — skip

          const isDivider = buffer.origin?.metadata?.isDivider === true;
          const dividerTitle = buffer.origin?.metadata?.dividerTitle as string | undefined;
          const threadTitle = buffer.origin?.metadata?.threadTitle as string | undefined;
          const nodeId = buffer.origin?.metadata?.nodeId as string | undefined;
          const sourcePlatform = buffer.origin?.sourcePlatform;

          const summary = isDivider
            ? (dividerTitle ?? 'Section')
            : cleanBufferName(
                threadTitle ?? buffer.text?.split('\n')[0]?.trim() ?? '',
                buffer.id.slice(0, 8)
              );

          const source = isDivider
            ? ''
            : [
                sourcePlatform,
                buffer.createdAt ? new Date(buffer.createdAt).toLocaleDateString() : null,
              ].filter(Boolean).join(' \u00b7 ');

          const wordCount = buffer.text
            ? buffer.text.replace(/<!--[^>]*-->/g, '').split(/\s+/).filter(Boolean).length
            : 0;

          restoredItems.push({
            bufferId: buffer.id,
            contentItem: {
              id: buffer.id,
              type: 'archive-node',
              text: buffer.text ?? '',
              metadata: {
                source: {
                  type: sourcePlatform ?? 'archive',
                  path: [threadTitle ?? 'Archive', nodeId ?? buffer.id],
                  nodeId,
                  threadId: buffer.origin?.metadata?.threadId as string | undefined,
                  threadTitle,
                },
                timestamp: buffer.createdAt,
                wordCount,
              },
            },
            summary,
            source,
            wordCount,
            sourceNodeId: nodeId,
            sortOrder: i,
            isDivider,
            dividerTitle,
          });
        }

        if (cancelled || restoredItems.length === 0) return;

        stackRestoredRef.current = true;
        setStackItems(restoredItems);
        setActiveStackIndex(0);
      } catch (err) {
        log.warn('Stack restoration failed:', err);
      }
    })();

    return () => { cancelled = true; };
  }, [isElectron, isConnected, activeProjectId, activeStackId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─────────────────────────────────────────────────────────────────────────
  // POLLING (for AUI sync) - ELECTRON: skip cloud polling
  // ─────────────────────────────────────────────────────────────────────────

  useEffect(() => {
    // ELECTRON: No cloud polling in local-only mode
    if (isElectron || !sessionId || pollInterval <= 0) return;

    const intervalId = setInterval(async () => {
      try {
        const buffer = await api.getBuffer(sessionId, activeBufferName);
        const serverContent = (buffer.workingContent ?? buffer.content ?? []) as ContentItem[];

        // Only update if content changed (compare by length for now)
        if (serverContent.length !== workingContent.length) {
          setWorkingContent(serverContent);
          // When syncing from server, reset dirty state
          setIsDirty(false);
        }
      } catch {
        // Ignore polling errors
      }
    }, pollInterval);

    return () => clearInterval(intervalId);
  }, [api, sessionId, activeBufferName, pollInterval, workingContent.length, isElectron]);

  // ─────────────────────────────────────────────────────────────────────────
  // CONTEXT VALUE
  // ─────────────────────────────────────────────────────────────────────────

  const value: BufferSyncContextType = useMemo(() => ({
    // State
    sessionId,
    isConnected,
    isConnecting,
    connectionError,
    activeBufferName,
    bufferType,
    workingContent,
    isDirty,
    buffers,
    syncError,
    history,
    canUndo,
    canRedo,
    currentVersionIndex,
    breadcrumbTrail,
    breadcrumbIndex,
    versionMap,
    activeVersionId,
    selectedText,
    focusedText,
    canGoBack: contentHistory.canGoBack,
    canGoForward: contentHistory.canGoForward,
    contentHistoryLabel: contentHistory.contentHistoryLabel,
    threadIndex,
    transformChain,
    activeChainBufferId,
    pendingHighlight,
    pendingMessageIndex,

    // Actions
    connect,
    disconnect,
    createBuffer,
    switchBuffer,
    deleteBuffer,
    refreshBuffers,
    getUserBuffers,
    getVoiceBuffer,
    pinBuffer,
    branchBuffer,
    switchBranch: switchBranchAction,
    rollbackBuffer: rollbackBufferAction,
    setContent,
    appendContent,
    clearContent,
    updateItem,
    removeItem,
    commit,
    undo,
    redo,
    checkout,
    importText: archiveImport.importText,
    importArchiveNode: archiveImport.importArchiveNode,
    importArchiveThread: archiveImport.importArchiveThread,
    importTranscription: archiveImport.importTranscription,
    getActiveBufferId: useCallback(() => activeBufferIdRef.current, []),
    getTextContent: archiveImport.getTextContent,
    transformAndCommit: transformChainOps.transformAndCommit,
    switchToChainVersion: transformChainOps.switchToChainVersion,
    refreshTransformChain: transformChainOps.refreshTransformChain,
    setSelectedText,
    setFocusedText,
    navigateTo,
    navigateBack,
    navigateForward,
    contentGoBack: contentHistory.contentGoBack,
    contentGoForward: contentHistory.contentGoForward,
    selectVersion,
    logEvent,
    openNodeInContext,
    goToPrevThread,
    goToNextThread,
    clearPendingNavigation,

    // Stack mode
    stackItems,
    activeStackIndex,
    addToStack: stackOps.addToStack,
    addDividerToStack: stackOps.addDividerToStack,
    removeFromStack: stackOps.removeFromStack,
    insertAtIndex: stackOps.insertAtIndex,
    updateStackItem: stackOps.updateStackItem,
    updateDividerTitle: stackOps.updateDividerTitle,
    reorderStack: stackOps.reorderStack,
    setActiveStackIndex,
    clearStack: stackOps.clearStack,
    setStackFromHydration: stackOps.setStackFromHydration,
  }), [
    sessionId, isConnected, isConnecting, connectionError,
    activeBufferName, bufferType, workingContent, isDirty, buffers, syncError, history,
    canUndo, canRedo, currentVersionIndex,
    breadcrumbTrail, breadcrumbIndex, versionMap, activeVersionId, selectedText, focusedText,
    contentHistory.canGoBack, contentHistory.canGoForward, contentHistory.contentHistoryLabel,
    transformChain, activeChainBufferId, threadIndex, pendingHighlight, pendingMessageIndex,
    stackItems, activeStackIndex,
    connect, disconnect, createBuffer, switchBuffer, deleteBuffer, refreshBuffers,
    getUserBuffers, getVoiceBuffer, pinBuffer, branchBuffer, switchBranchAction, rollbackBufferAction,
    setContent, appendContent, clearContent, updateItem, removeItem,
    commit, undo, redo, checkout,
    archiveImport.importText, archiveImport.importArchiveNode, archiveImport.importArchiveThread, archiveImport.importTranscription, archiveImport.getTextContent,
    transformChainOps.transformAndCommit, transformChainOps.switchToChainVersion, transformChainOps.refreshTransformChain,
    setSelectedText, setFocusedText, navigateTo, navigateBack, navigateForward,
    contentHistory.contentGoBack, contentHistory.contentGoForward,
    selectVersion, logEvent,
    openNodeInContext, goToPrevThread, goToNextThread, clearPendingNavigation,
    stackOps.addToStack, stackOps.addDividerToStack, stackOps.removeFromStack, stackOps.insertAtIndex,
    stackOps.updateStackItem, stackOps.updateDividerTitle, stackOps.reorderStack, stackOps.clearStack, stackOps.setStackFromHydration,
  ]);

  return (
    <BufferSyncContext.Provider value={value}>
      {children}
    </BufferSyncContext.Provider>
  );
}

export default BufferSyncContext;
